"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocalization } from "../../lib/translation";
import { Button, Progress } from "@digitaltableteur/react";
import DonnyBookingEmbed from "@dt/DonnyBookingEmbed";
import Tabs, { getTabPanelProps, type TabItem } from "@dt/Tabs";
import {
  resolveSiteBookingConfig,
  type SiteBookingConfig,
} from "../../lib/donny-booking";
import styles from "./ContactInquiryPanel.module.css";

export type ContactInquiryMode = "message" | "book";

export interface ContactInquiryPanelProps {
  /**
   * Tab to show; defaults to message. The panel switches to it again whenever
   * the prop value changes after mount.
   * @default "message" 
   */
  initialMode?: ContactInquiryMode;
  /** Optional pricing package id for booking prefill. */
  packageId?: string;
  /** Editorial contact form slot shown on the message tab. */
  messagePanel: ReactNode;
  /**
   * Booking config used only when packageId is not set; packageId wins and this
   * prop is then ignored. Without either, the site default booking config
   * applies.
   */
  bookingConfig?: SiteBookingConfig;
}

export interface ContactInquiryPanelHandle {
  /** Switch to the message tab and scroll the viewport to the form. */
  scrollToMessageForm: () => void;
  /** Switch to the book-a-call tab, scroll the panel into view and focus that tab. */
  scrollToBooking: () => void;
}

/** Contact page panel: message tab + book-a-call tab with composed form slot. */
export const ContactInquiryPanel = forwardRef<
  ContactInquiryPanelHandle,
  ContactInquiryPanelProps
>(function ContactInquiryPanel(
  { initialMode, packageId, messagePanel, bookingConfig },
  ref,
) {
  const {
    translate: t,
    language,
    resolvedLanguage,
  } = useLocalization();
  const activeLanguage = resolvedLanguage || language;
  // The message form is the default (its first field takes focus); booking
  // opens from a ?mode=book deep link, such as the "Book a call" buttons.
  const [mode, setMode] = useState<ContactInquiryMode>(initialMode ?? "message");

  useEffect(() => {
    if (initialMode === "book" || initialMode === "message") {
      setMode(initialMode);
    }
  }, [initialMode]);

  const booking = useMemo(() => {
    if (packageId) {
      return resolveSiteBookingConfig({ packageId });
    }
    return bookingConfig ?? resolveSiteBookingConfig({});
  }, [bookingConfig, packageId]);
  const bookingLoadingFallback = useMemo(
    () => (
      <div className={styles.bookingLoading}>
        <Progress
          indeterminate
          size="sm"
          state="info"
          label={t("donnyBookingLoading", "Loading booking calendar")}
        />
      </div>
    ),
    [activeLanguage, t],
  );

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        key: "message",
        label: t("contactInquiryMessageTab", "Send a message"),
      },
      {
        key: "book",
        label: t("contactInquiryBookTab", "Book a call"),
      },
    ],
    [activeLanguage, t],
  );

  const handleSelectMessage = useCallback(() => {
    setMode("message");
  }, []);

  const handleModeChange = useCallback((key: string) => {
    if (key === "message" || key === "book") {
      setMode(key);
    }
  }, []);

  const scrollToMessageForm = useCallback(() => {
    setMode("message");
    requestAnimationFrame(() => {
      const formRoot = document.getElementById("contact-form");
      if (!formRoot) return;

      formRoot.scrollIntoView({ behavior: "smooth", block: "start" });

      const firstField = formRoot.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
      );
      firstField?.focus({ preventScroll: true });
    });
  }, []);

  const scrollToBooking = useCallback(() => {
    setMode("book");
    requestAnimationFrame(() => {
      const panel = document.getElementById("contact-inquiry");
      if (!panel) return;
      panel.scrollIntoView?.({ behavior: "smooth", block: "start" });
      panel
        .querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
        ?.focus({ preventScroll: true });
    });
  }, []);

  useImperativeHandle(ref, () => ({ scrollToMessageForm, scrollToBooking }), [
    scrollToMessageForm,
    scrollToBooking,
  ]);

  return (
    <div
      className={styles.inquiryPanel}
      id="contact-inquiry"
      data-donny-target="contact.inquiry"
    >
      <div className={styles.modeSwitch}>
        <Tabs
          tabs={tabs}
          activeTab={mode}
          onTabChange={handleModeChange}
          ariaLabel={t("contactInquiryModeLabel", "Contact options")}
          variant="underline"
          size="sm"
          className={styles.modeTabs}
        />
      </div>

      <div className={styles.panelBody}>
        <div
          {...getTabPanelProps("message", mode === "message")}
          data-donny-target="contact.form"
        >
          <div id="contact-form" className={styles.contactFormAnchor}>
            {messagePanel}
          </div>
        </div>
        <div
          {...getTabPanelProps("book", mode === "book")}
          data-donny-target="contact.booking"
        >
          <div id="contact-booking">
            {booking.configured ? (
              <DonnyBookingEmbed
                {...booking}
                loadingFallback={bookingLoadingFallback}
                headingLevel="h2"
              />
            ) : (
              <div className={styles.unconfigured}>
                <p className={styles.unconfiguredText}>
                  {t(
                    "contactBookingUnavailable",
                    "The calendar didn't load. Send a message and a reply with times follows within one business day.",
                  )}
                </p>
                <div className={styles.unconfiguredActions}>
                  <Button
                    variant="primary"
                    size="lg"
                    className={styles.actionButton}
                    onClick={handleSelectMessage}
                  >
                    {t("contactBookingUseForm", "Use the contact form")}
                  </Button>
                  <Button
                    href="mailto:mail@digitaltableteur.com"
                    variant="secondary"
                    size="lg"
                    className={styles.actionButton}
                  >
                    {t("contactBookingEmail", "Send an email instead")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

ContactInquiryPanel.displayName = "ContactInquiryPanel";

export default ContactInquiryPanel;
